import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../data/mock";
import { useLang } from "../context/LanguageContext";

const Lightbox = ({ items, active, onClose, onNext, onPrev }) => {
  const { t } = useLang();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft") onPrev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNext, onPrev]);

  const item = items[active];
  const caption = item.captionKey ? t(`gallery_captions.${item.captionKey}`) : item.caption || "";

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close" className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
        <X size={22} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Previous" className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-[#800000] text-white flex items-center justify-center transition-colors">
        <ChevronLeft size={26} />
      </button>
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={item.src} alt={caption} className="w-full max-h-[80vh] object-contain rounded-md shadow-2xl" />
        <p className="text-center text-white mt-4 text-sm md:text-base">
          {caption}
          <span className="text-slate-400 ml-2">({active + 1} / {items.length})</span>
        </p>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Next" className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-[#800000] text-white flex items-center justify-center transition-colors">
        <ChevronRight size={26} />
      </button>
    </div>
  );
};

export const useLightbox = (items) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const openAt = useCallback((i) => {
    setActive(i);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const next = useCallback(() => setActive((i) => (i + 1) % items.length), [items.length]);
  const prev = useCallback(() => setActive((i) => (i - 1 + items.length) % items.length), [items.length]);

  const node = open ? <Lightbox items={items} active={active} onClose={close} onNext={next} onPrev={prev} /> : null;
  return { openAt, lightbox: node };
};

export default Lightbox;
export { GALLERY };
